
import { Search, ArrowRight, Star, X, Zap } from 'lucide-react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useRouter } from '@/lib/navigation';
import { allArticles, categoryColors, type ArticleWithCategory } from '@/lib/articles';
import { getGuideHref } from '@/lib/guide-routes';
import { HeroBackdropLayers, heroDescriptionClass } from '@/components/help-center/latest-power-shared';
import HeroFloatingSmilies from '@/components/help-center/HeroFloatingSmilies';
import InlineSearchDropdown from '@/components/help-center/InlineSearchDropdown';
import PowerSearchResultRow from '@/components/help-center/powers/PowerSearchResultRow';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { cn } from '@/lib/utils';
import { useCms } from '@/components/painel/cms/cms-context';
import { filterPowersByQuery, type PowerInfo } from '@/lib/powers-data';
import { highlightSearchText } from '@/lib/search-highlight';
import { useTrackSearch } from '@/hooks/use-track-search';
import { usePopularSearches, type PopularSearchChip } from '@/hooks/use-popular-searches';

const searchExamples = [
  'Como criar uma conta no xat?',
  'Redefinir minha senha...',
  'O que são Powers?',
  'Como denunciar um usuário?',
  'Criar uma sala de chat...',
];

interface HeroProps {
  onSelectCategory?: (category: string, articleId?: number) => void;
  compact?: boolean;
  /**
   * Na homepage mobile o Hero sobe para o fim da página como banner CTA.
   * Reduz padding e esconde smilies flutuantes abaixo de `lg` (desktop inalterado).
   */
  mobileBanner?: boolean;
}

type HeroSearchItem =
  | { kind: 'article'; article: ArticleWithCategory }
  | { kind: 'power'; power: PowerInfo };

const DROPDOWN_LIMIT = 8;
const ARTICLE_DROPDOWN_LIMIT = 5;

export default function Hero({
  onSelectCategory,
  compact = false,
  mobileBanner = false,
}: HeroProps) {
  const router = useRouter();
  const { powers: catalogPowers } = useCatalogPowers();
  const { site, siteReady } = useCms();
  const { hero } = site;
  const cmsPopularFallback = useMemo<PopularSearchChip[]>(() => {
    const chips: PopularSearchChip[] = [];
    if (hero.primaryButtonLabel?.trim() && hero.primarySearchQuery?.trim()) {
      chips.push({
        query: hero.primarySearchQuery.trim(),
        label: hero.primaryButtonLabel.trim(),
        count: 0,
      });
    }
    if (hero.secondaryButtonLabel?.trim() && hero.secondarySearchQuery?.trim()) {
      chips.push({
        query: hero.secondarySearchQuery.trim(),
        label: hero.secondaryButtonLabel.trim(),
        count: 0,
      });
    }
    return chips;
  }, [
    hero.primaryButtonLabel,
    hero.primarySearchQuery,
    hero.secondaryButtonLabel,
    hero.secondarySearchQuery,
  ]);
  const { items: popularSearches } = usePopularSearches(cmsPopularFallback);
  const [searchQuery, setSearchQuery] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const articleMatches = useMemo(() => {
    if (searchQuery.trim().length < 2) return [];
    const q = searchQuery.trim().toLowerCase();
    return [...allArticles]
      .filter((a) => a.title.toLowerCase().includes(q))
      .sort((a, b) => {
        const ta = a.title.toLowerCase();
        const tb = b.title.toLowerCase();
        const score = (t: string) =>
          t === q ? 1000 : t.startsWith(q) ? 800 : t.includes(q) ? 600 : 0;
        return score(tb) - score(ta) || ta.length - tb.length;
      });
  }, [searchQuery]);

  const powerMatches = useMemo(
    () =>
      searchQuery.trim().length >= 2
        ? filterPowersByQuery(catalogPowers, searchQuery)
        : [],
    [catalogPowers, searchQuery]
  );

  useTrackSearch(
    searchQuery,
    articleMatches.length + powerMatches.length,
    searchQuery.trim().length >= 2
  );

  const flatItems = useMemo((): HeroSearchItem[] => {
    const q = searchQuery.trim().toLowerCase();
    type Ranked = { score: number; item: HeroSearchItem };
    const ranked: Ranked[] = [];

    for (const article of articleMatches.slice(0, ARTICLE_DROPDOWN_LIMIT)) {
      const t = article.title.toLowerCase();
      const score =
        (t === q ? 1000 : t.startsWith(q) ? 800 : t.includes(q) ? 600 : 100) - 5;
      ranked.push({ score, item: { kind: 'article', article } });
    }

    for (const [i, power] of powerMatches.entries()) {
      const name = power.name.toLowerCase();
      const slug = power.slug.toLowerCase();
      let score = 0;
      if (name === q || slug === q) score = 1000;
      else if (name.startsWith(q) || slug.startsWith(q)) score = 800;
      else if (name.includes(q) || slug.includes(q)) score = 600;
      else score = 100 - i;
      ranked.push({ score, item: { kind: 'power', power } });
    }

    ranked.sort((a, b) => b.score - a.score);
    return ranked.slice(0, DROPDOWN_LIMIT).map((r) => r.item);
  }, [articleMatches, powerMatches, searchQuery]);

  const totalCount = articleMatches.length + powerMatches.length;
  const visibleArticleCount = flatItems.filter((item) => item.kind === 'article').length;
  const visiblePowerCount = flatItems.filter((item) => item.kind === 'power').length;

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      setShowDropdown(true);
      setActiveIndex(-1);
    } else {
      setShowDropdown(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectArticle = useCallback(
    (article: ArticleWithCategory) => {
      setShowDropdown(false);
      setSearchQuery('');
      router.push(getGuideHref(article.category, article.slug));
    },
    [router]
  );

  const handleSelectPower = useCallback(
    (slug: string) => {
      setShowDropdown(false);
      setSearchQuery('');
      router.push(`/powers/${slug}`);
    },
    [router]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const item = flatItems[activeIndex];
      if (!item) return;
      if (item.kind === 'article') handleSelectArticle(item.article);
      else handleSelectPower(item.power.slug);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    if (searchQuery.trim().length > 0) return;
    const currentExample = searchExamples[currentExampleIndex];
    const typingSpeed = isDeleting ? 50 : 100;

    if (!isDeleting && charIndex < currentExample.length) {
      const timeout = setTimeout(() => {
        setPlaceholderText(currentExample.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, typingSpeed);
      return () => clearTimeout(timeout);
    } else if (isDeleting && charIndex > 0) {
      const timeout = setTimeout(() => {
        setPlaceholderText(currentExample.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      }, typingSpeed);
      return () => clearTimeout(timeout);
    } else if (!isDeleting && charIndex === currentExample.length) {
      const timeout = setTimeout(() => setIsDeleting(true), 2000);
      return () => clearTimeout(timeout);
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setCurrentExampleIndex((currentExampleIndex + 1) % searchExamples.length);
    }
  }, [charIndex, isDeleting, currentExampleIndex, searchQuery]);

  return (
    <section className="relative z-30 overflow-x-hidden bg-[#030023]">
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <HeroBackdropLayers className="absolute inset-0" />
      </div>

      {!compact && (
        <div className={cn(mobileBanner && 'hidden lg:block')}>
          <HeroFloatingSmilies />
        </div>
      )}

      <div
        className={cn(
          'pointer-events-none relative z-[20] mx-auto max-w-5xl px-6 transition-all duration-300',
          compact
            ? 'pb-10 pt-28 md:pb-12 md:pt-32'
            : mobileBanner
              ? 'pb-14 pt-16 lg:pb-28 lg:pt-52 xl:pb-36 xl:pt-64'
              : 'pb-28 pt-52 md:pb-36 md:pt-64'
        )}
      >
        {!compact && (
          <div className={cn('mb-12 text-center', mobileBanner && 'mb-8 lg:mb-12')}>
            <h1
              className={cn(
                'mb-6 font-poppins text-5xl font-bold tracking-tight text-white md:text-7xl',
                mobileBanner && 'text-4xl sm:text-5xl lg:text-7xl',
                !siteReady && 'invisible'
              )}
            >
              {hero.title}
            </h1>
            <p
              className={cn(
                'mx-auto max-w-2xl',
                heroDescriptionClass,
                !siteReady && 'invisible'
              )}
            >
              {hero.description}
            </p>
          </div>
        )}

        <div className="pointer-events-auto max-w-3xl mx-auto py-1" ref={containerRef}>
          <div className="relative isolate z-10 rounded-2xl group">
            {/* Halo atrás da barra (não fica cortado pelo clip do fundo) */}
            <div
              className="pointer-events-none absolute -inset-3 rounded-[1.35rem] bg-[#2563eb] opacity-0 blur-2xl transition-opacity duration-300 ease-out group-focus-within:opacity-[0.45]"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -inset-2 rounded-[1.2rem] bg-[#000b6a] opacity-0 blur-xl transition-opacity duration-300 ease-out group-focus-within:opacity-50"
              aria-hidden
            />

            {/* Barra de pesquisa — tamanho fixo, nunca muda */}
            <div
              ref={searchBarRef}
              className={cn(
                'relative flex items-center gap-4 px-6 py-4 bg-white rounded-2xl border border-blue-100/90 shadow-2xl shadow-blue-900/15',
                'transition-[border-color,box-shadow] duration-300 ease-out',
                'focus-within:border-[#000b6a]/45',
                'focus-within:shadow-[0_0_0_2px_rgba(0,11,106,0.12),0_0_24px_4px_rgba(0,11,106,0.35),0_0_56px_12px_rgba(0,11,106,0.22)]'
              )}
            >
              <Search className="w-6 h-6 shrink-0 text-gray-400 transition-colors duration-300 group-focus-within:text-[#000b6a]" />
              <input
                ref={inputRef}
                type="text"
                  placeholder={placeholderText}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => searchQuery.trim().length >= 2 && setShowDropdown(true)}
                className="w-full bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-lg font-poppins"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setShowDropdown(false); inputRef.current?.focus(); }}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
              <kbd className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium flex-shrink-0">
                <span className="text-xs">⌘</span>K
              </kbd>
            </div>
          </div>

          <InlineSearchDropdown
            open={showDropdown}
            anchorRef={searchBarRef}
            dropdownRef={dropdownRef}
            query={searchQuery}
            totalCount={totalCount}
            visibleCount={flatItems.length}
          >
            {visibleArticleCount > 0 ? (
              <div>
                <p className="border-b border-gray-50 bg-gray-50/80 px-6 py-2 font-poppins text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Artigos
                </p>
                <ul>
                  {flatItems
                    .map((item, index) => ({ item, index }))
                    .filter(
                      (entry): entry is { item: { kind: 'article'; article: ArticleWithCategory }; index: number } =>
                        entry.item.kind === 'article'
                    )
                    .map(({ item, index }) => {
                      const article = item.article;
                      return (
                        <li
                          key={`${article.category}-${article.id}`}
                          className="border-b border-gray-50 last:border-b-0"
                        >
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectArticle(article)}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`group flex w-full items-center gap-5 px-6 py-4 text-left transition-colors ${
                              activeIndex === index ? 'bg-blue-50' : 'hover:bg-blue-50'
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="mb-2 flex items-center gap-2">
                                {article.popular ? (
                                  <Star className="h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500" />
                                ) : null}
                                <span className="line-clamp-1 font-poppins text-sm font-semibold text-gray-900">
                                  {highlightSearchText(article.title, searchQuery)}
                                </span>
                              </div>
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${categoryColors[article.category]}`}
                              >
                                {article.categoryName}
                              </span>
                            </div>
                            <ArrowRight
                              className={`h-4 w-4 shrink-0 transition-all ${
                                activeIndex === index
                                  ? 'translate-x-0.5 text-blue-500'
                                  : 'text-gray-300 group-hover:translate-x-0.5 group-hover:text-blue-400'
                              }`}
                            />
                          </button>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ) : null}

            {visiblePowerCount > 0 ? (
              <div>
                <p className="flex items-center gap-1.5 border-b border-gray-50 bg-gray-50/80 px-6 py-2 font-poppins text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  <Zap className="h-3 w-3 fill-amber-400 text-amber-500" aria-hidden />
                  Powers
                </p>
                <ul>
                  {flatItems
                    .map((item, index) => ({ item, index }))
                    .filter(({ item }) => item.kind === 'power')
                    .map(({ item, index }) => {
                      if (item.kind !== 'power') return null;
                      return (
                      <PowerSearchResultRow
                        key={item.power.slug}
                        power={item.power}
                        query={searchQuery}
                        active={activeIndex === index}
                        onSelect={() => handleSelectPower(item.power.slug)}
                        onHover={() => setActiveIndex(index)}
                      />
                      );
                    })}
                </ul>
              </div>
            ) : null}
          </InlineSearchDropdown>

          {!compact && popularSearches.length > 0 ? (
            <div
              className={cn(
                'mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-blue-50 sm:gap-3',
                !siteReady && 'invisible'
              )}
            >
              <span className="shrink-0">Mais buscados:</span>
              {popularSearches.map((chip) => (
                <button
                  key={chip.query}
                  type="button"
                  onClick={() => {
                    setSearchQuery(chip.query);
                    inputRef.current?.focus();
                  }}
                  className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm transition-colors hover:bg-white/20"
                >
                  {chip.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
